import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import i18n from '@/i18n';

import Button from '@/components/button/Button';
import WarningField from '@/components/warningField/WarningField';
import { StepperBase } from '@/components/stepper/Stepper';

import { ScheduleDTO } from '@/data/DTOs/schedule/ScheduleDTO';
import { useAppointmentsService } from '@/data/services/useAppointmentsService';
import { AppError } from '@/utils/AppError';
import { useAuthStore } from '@/stores/AuthStore';

import { useSchedulingSteps } from './useSchedulingSteps';
import { PageBase } from '../PageBase';
import { SchedulingBase } from './SchedulingBase';
import {
  ScheduleAppointmentType,
  SchedulingConfirmInformationInterface,
  SchedulingStepsInterface,
} from './SchedulingInterface';
import { DialogFailurePayment } from './schedulingConfirm/DialogFailurePayment';
import { styles } from './styles';

const Scheduling = () => {
  const { t } = useTranslation([
    'signInPersonalInfo',
    'signInAddress',
    'success',
    'schedule',
    'common',
    'appointmentDetails',
  ]);
  const navigate = useNavigate();
  const location = useLocation();
  const { appointmentId } = useParams();
  const { user } = useAuthStore();
  const { appointmentEdit } = location.state || {};
  const [currentStep, setCurrentStep] = useState(
    appointmentId == undefined ? 0 : 2
  );
  const [inAddon, setIsAddon] = useState(false);
  const [rescheduleObject, setRescheduleObject] = useState(appointmentEdit);
  const [loading, setLoading] = useState(false);
  const [showPopUp, setShowPopUp] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [showDialogFailure, setShowDialogFailure] = useState(false);

  const [warningMessage, setWarningMessage] = useState<string>('');
  const [rightButtonTitle, setRightButtonTitle] = useState<string>(
    t('common:close')
  );
  const [leftButtonTitle, setLeftButtonTitle] = useState<string>('');
  const [rightButtonAction, setRightButtonAction] = useState(() => {});
  const [leftButtonAction, setLeftButtonAction] = useState(() => {});
  const [isValid, setIsValid] = useState(true);
  const { scheduleAppointment, rescheduleAppointment, getAppointmentDetails } =
    useAppointmentsService();
  const reschedule = rescheduleObject != undefined;

  const defaultUserName = `${user?.name ?? 'Nome não informado'} ${
    user?.surname ?? ''
  }`;

  const isOnlineWithDoctorSelected =
    reschedule &&
    Number(rescheduleObject?.appointmentTypeId) === 2 &&
    !!rescheduleObject?.doctor?.id &&
    rescheduleObject?.doctor.firstName !==
      'schedule:screen_choose_doctor_no_prefence';

  const [schedule, setSchedule] = useState<ScheduleDTO>({
    appointmentTypeId: reschedule
      ? Number(rescheduleObject?.appointmentTypeId)
      : 0,
    date: rescheduleObject?.date ?? '',
    locationId: isOnlineWithDoctorSelected
      ? undefined
      : rescheduleObject?.location?.id ?? '',
    resourceId: rescheduleObject?.doctor.id ?? '-',
    subId: rescheduleObject?.subId,
    time: rescheduleObject?.time ?? '',
    id: rescheduleObject?.id,
    creditCardId: rescheduleObject?.creditCard ?? undefined,
    userDependentId: rescheduleObject?.userDependentId ?? undefined,
    categoryId: rescheduleObject?.categoryId ?? undefined,
  });

  const [internalObject, setInternalObject] =
    useState<SchedulingConfirmInformationInterface>({
      patientName: reschedule
        ? rescheduleObject?.userDependentId
          ? `${rescheduleObject.dependent.name} ${rescheduleObject.dependent.surname}`
          : defaultUserName
        : '',
      patientPhoto: '',
      scheduleType: reschedule
        ? rescheduleObject?.appointmentTypeId?.toString() ?? ''
        : '',
      address: rescheduleObject?.location.name,
      doctorName:
        rescheduleObject?.doctor.firstName +
        ' ' +
        rescheduleObject?.doctor.lastName,
      date: rescheduleObject?.date,
      time: rescheduleObject?.time,
      passedDateSelect: false,
      paymentType: rescheduleObject?.banner
        ? t('schedule:screen_choose_payment_card')
        : t('schedule:screen_choose_pay_locally'),
      creditCard: rescheduleObject?.creditCard,
      price: '',
      addressWhatsapp: '',
      banner: rescheduleObject?.banner || '',
      cardLast4Digits: rescheduleObject?.cardLast4Digits || '',
      categoryId: rescheduleObject?.categoryId || '',
    });

  useEffect(() => {
    console.log('SCHEDULE:::', schedule);
    console.log('INTERNAL::STEPPER::', internalObject);
  }, [schedule]);

  const { schedulingSteps, addonSteps } = useSchedulingSteps();
  const [steps, setSteps] = useState(schedulingSteps);

  async function changeStep(nextStep: boolean) {
    console.log('SCHEDULE AO MUDAR STEP:', schedule);
    cleanUpSetStates();
    if (inAddon) {
      changeInternalStep(nextStep);
      return;
    }
    if (nextStep) {
      if (currentStep < steps.length - 1) {
        if (
          steps[currentStep].nextPopUp &&
          !showPopUp &&
          !ScheduleAppointmentType.isOnline(schedule.appointmentTypeId)
        ) {
          setShowPopUp(true);
          return;
        }
        if (reschedule && currentStep == 3) {
          setCurrentStep(currentStep + 2);
          return;
        }
        if (currentStep == 3)
          setInternalObject({ ...internalObject, passedDateSelect: true });
        setCurrentStep(currentStep + 1);
      } else {
        reschedule ? sendReScheduleAppointment() : sendScheduleAppointment();
      }
    } else {
      if (reschedule) {
        if (currentStep == 5) {
          setCurrentStep(3);
          return;
        }
        if (currentStep == 4) {
          setCurrentStep(3);
          return;
        }
        if (currentStep == 3) {
          setCurrentStep(2);
          return;
        }
        if (currentStep == 2) {
          navigate(-1);
          return;
        }
      }

      if (currentStep == 1 && reschedule) navigate(-1);
      if (currentStep == 0) {
        navigate('/');
      } else {
        setCurrentStep(currentStep - 1);
      }
    }
  }

  function changeInternalStep(next: boolean) {
    console.log('SCHEDULE AO MUDAR STEP:', schedule);
    const step = addonSteps.find((addon) => {
      return next
        ? addon.pageLoading == steps[currentStep].addonNext
        : addon.pageLoading == steps[currentStep].addonBack;
    });
    if (step) {
      navigateToAddon(step, next);
    } else {
      backToRegularSteps();
      setIsAddon(false);
    }
  }

  function navigateToAddon(addon: SchedulingStepsInterface, next: boolean) {
    if (!next) {
      backToRegularSteps();
      return;
    }
    const updateSteps = [...steps];
    const addonStep = addon;
    updateSteps.splice(currentStep, 0, addonStep);
    setSteps(updateSteps);
    setIsAddon(true);
  }

  function backToRegularSteps() {
    const updateSteps = [...steps];
    updateSteps.splice(currentStep, 1);
    setSteps(updateSteps);
  }

  function fetchDetails() {
    setLoading(true);
    if (appointmentId) {
      getAppointmentDetails(appointmentId)
        .then((data) => {
          if (data.id == null) {
            navigate('/home', { state: { showErrorGetReschedule: true } });
            return;
          }
          setRescheduleObject(data);
        })
        .catch(() => {
          navigate('/home', { state: { showErrorGetReschedule: true } });
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }

  function cleanUpSetStates() {
    setShowWarning(false);
    setWarningMessage('');
    setRightButtonTitle(t('common:close'));
    setLeftButtonTitle('');
    setRightButtonAction();
    setLeftButtonAction();
    setLoading(false);
  }

  function sendScheduleAppointment() {
    setLoading(true);
    //debugger;
    scheduleAppointment(schedule)
      .then(() => navigate('/home', { state: { showScheduleSuccess: true } }))
      .catch((error: AppError) => {
        console.log('error>>>', error);

        if (
          error.message?.includes('INV CARD NO') ||
          error.message?.toLowerCase().includes('deny your card')
        ) {
          setShowDialogFailure(true);
        } else {
          setWarningMessage(t(error.message));
          setShowWarning(true);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  function sendReScheduleAppointment() {
    setLoading(true);
    console.log('schedule original:', schedule);
    console.log('schedule serializado:', JSON.stringify(schedule, null, 2));

    const payload = {
      appointmentTypeId: schedule.appointmentTypeId,
      date: schedule.date,
      locationId: schedule.locationId,
      resourceId: schedule.resourceId,
      subId: schedule.subId,
      time: schedule.time,
      id: schedule.id,
      creditCardId: schedule.creditCardId || undefined,
      userDependentId: schedule.userDependentId || undefined,
      categoryId: schedule.categoryId || undefined,
    };
    console.log('payload a ser enviado:', JSON.stringify(payload, null, 2));
    //debugger;
    rescheduleAppointment(payload)
      .then(() => navigate('/home', { state: { showReScheduleSuccess: true } }))
      .catch((error: AppError) => {
        setWarningMessage(t(error.message));
        setShowWarning(true);
      })
      .finally(() => {
        setLoading(false);
      });
  }

  useEffect(() => {
    if (appointmentId && !appointmentEdit) {
      fetchDetails();
    }
  }, []);

  useEffect(() => {
    setSteps(steps);
  }, [i18n.language, internalObject]);

  const adjustedSteps = reschedule ? steps.slice(0, 3) : steps;

  return (
    <PageBase
      title={
        reschedule
          ? t('schedule:reschedule_title')
          : steps[currentStep].title ?? ''
      }
      onBackClick={() => changeStep(false)}
      paddingInline={0}
      sxContainer={styles.minContainer}
      sxContentBox={styles.minContent}
      sxHeader={styles.minHeader}
    >
      {currentStep != 0 && !inAddon && (
        <StepperBase
          steps={adjustedSteps.length - 1}
          currentStep={currentStep - 1}
        />
      )}
      <Typography sx={styles.subTitle}>
        {steps[currentStep].subTitle}
      </Typography>
      <SchedulingBase
        page={steps[currentStep].page}
        pageLoading={steps[currentStep].pageLoading}
        nextPopUp={steps[currentStep].nextPopUp}
        showPopUp={showPopUp}
        setShowPopUp={setShowPopUp}
        setNextStep={changeStep}
        setSchedule={setSchedule}
        schedule={schedule}
        loading={loading}
        setLoading={setLoading}
        setShowWarning={setShowWarning}
        setWarningMessage={setWarningMessage}
        setIsValid={setIsValid}
        setRightButtonTitle={setRightButtonTitle}
        setRightButtonAction={setRightButtonAction}
        setLeftButtonTitle={setLeftButtonTitle}
        setLeftButtonAction={setLeftButtonAction}
        internalObject={internalObject}
        setInternalObject={setInternalObject}
        setCurrentStep={setCurrentStep}
        changeInternalStep={changeInternalStep}
      />
      <Box sx={styles.warningBox}>
        <WarningField
          description={warningMessage}
          isHidden={!showWarning}
          setShowWarning={setShowWarning}
          status="warning"
          leftButtonTitle={leftButtonTitle}
          leftButtonAction={
            leftButtonTitle == ''
              ? null
              : () => {
                  setShowWarning(false);
                  leftButtonAction;
                }
          }
          rightButtonTitle={rightButtonTitle}
          rightButtonAction={() => {
            setShowWarning(false);
            rightButtonAction;
          }}
        />
      </Box>
      {!inAddon && (
        <Button
          title={
            currentStep == adjustedSteps.length - 1
              ? t('schedule:screen_schedule_confirm')
              : t('common:next')
          }
          onClick={() => changeStep(true)}
          disable={!isValid || loading}
          loading={loading}
        />
      )}
      {showDialogFailure && (
        <DialogFailurePayment
          showPopUp={showDialogFailure}
          setShowPopUp={setShowDialogFailure}
          buttonActionArray={[() => setShowDialogFailure(false)]}
        />
      )}
    </PageBase>
  );
};

export default Scheduling;
